import React, { lazy, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { hasValue } from 'globalutils/fmUtils';
import { getCurrentDriverLabel } from './udReassignmentUtils';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);
const Modal = require(`globalcomponents/UI/Modal/${layout}`);
const SuccessIcon = lazy(() => import(`globalcomponents/SuccessIcon/${layout}`));

const REASSIGN_API_ID = 'createUdReassignment';

const UdReassignmentFormContent = ({
  entry,
  companyDrivers,
  logIdPub,
  i18n,
  createUdReassignment,
  getDailyLogDetails,
  fetchDailyLogDetailsApiId,
  apiStatus,
  handleClose,
  setSuccessOpen,
  setOpen,
}) => {
  const [annotation, setAnnotation] = useState('');
  const hasReassignApiStatus = hasValue(apiStatus?.status, 'api', REASSIGN_API_ID);
  const isRequestEnabled = annotation.trim().length > 0;

  useEffect(() => {
    setAnnotation('');
  }, [entry?.id, entry?.eventId]);

  const handleSubmit = () => {
    if (!isRequestEnabled || hasReassignApiStatus?.isLoading || !createUdReassignment) return;

    createUdReassignment({
      apiId: REASSIGN_API_ID,
      data: {
        id: logIdPub,
        body: {
          entryId: entry?.id ?? entry?.entryId,
          eventId: entry?.eventId || '',
          minutesFrom: entry?.minutes_from,
          minutesTo: entry?.minutes_to,
          annotation: annotation.trim(),
        },
      },
      callback: () => {
        setOpen(false);
        setSuccessOpen(true);
        getDailyLogDetails({
          data: { id: logIdPub },
          apiId: fetchDailyLogDetailsApiId,
        });
        setAnnotation('');
      },
    });
  };

  const eventFields = [
    { label: i18n.t('logs.timeFrom'), value: entry?.time_from || '-' },
    { label: i18n.t('logs.timeTo'), value: entry?.time_to || '-' },
    { label: i18n.t('logs.location'), value: entry?.location || '-' },
    { label: i18n.t('logs.notes'), value: entry?.note || '-' },
    {
      label: i18n.t('common.currentDriver'),
      value: getCurrentDriverLabel(entry, companyDrivers),
      fullWidth: true,
    },
  ];

  return (
    <div className="successpopup udReassignModal">
      <div className="reassignModalHeader">
        <h2>{i18n.t('logs.reassignToUdHeading')}</h2>
      </div>

      <div className="reassignSection">
        <h3 className="reassignSectionTitle">{i18n.t('logs.reassignEventDetails')}</h3>
        <div className="reassignEventCard">
          <div className="reassignEventCardHeader">
            <span className="reassignEventStatus">{entry?.status || 'DRIVING'}</span>
          </div>
          <div className="reassignEventGrid">
            {eventFields.map((item) => (
              <div
                key={item.label}
                className={`reassignEventField${item.fullWidth ? ' reassignEventFieldFull' : ''}`}
              >
                <span className="reassignEventLabel">{item.label}</span>
                <span className="reassignEventValue">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="reassignSection reassignSectionForm">
        <h3 className="reassignSectionTitle">{i18n.t('logs.reassignRequestDetails')}</h3>
        <FormStyle.default.FieldsWrapper width="100%" className="reassignField">
          <Label variant="body1">{i18n.t('logs.destinationCoDriver')}</Label>
          <FormStyle.default.TextWrapper>
            <Input value={i18n.t('logs.reassignToUd')} disabled type="text" />
          </FormStyle.default.TextWrapper>
        </FormStyle.default.FieldsWrapper>
        <FormStyle.default.FieldsWrapper width="100%" className="reassignField annotationField">
          <Label variant="body1">{i18n.t('common.annotation')}</Label>
          <FormStyle.default.TextareaWrapper>
            <Input
              placeholder={i18n.t('common.annotation')}
              value={annotation}
              isNotDebounced
              handleOnChange={(value) => setAnnotation(value)}
              multiLine
              type="text"
            />
          </FormStyle.default.TextareaWrapper>
        </FormStyle.default.FieldsWrapper>
      </div>

      {hasReassignApiStatus?.errorMessage && (
        <Message type="error" message={i18n.t(hasReassignApiStatus.errorMessage)} />
      )}

      <div className="deletePopupBtn reassignModalActions">
        <div className="cancelBtn">
          <Button label={i18n.t('common.cancel')} type="reset" onClick={handleClose} />
        </div>
        <div className="deletebtn">
          <Button
            label={i18n.t('logs.sendRequest')}
            disabled={!isRequestEnabled || hasReassignApiStatus?.isLoading}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

UdReassignmentFormContent.propTypes = {
  entry: PropTypes.object,
  companyDrivers: PropTypes.object,
  logIdPub: PropTypes.string,
  i18n: PropTypes.object.isRequired,
  createUdReassignment: PropTypes.func,
  getDailyLogDetails: PropTypes.func.isRequired,
  fetchDailyLogDetailsApiId: PropTypes.string,
  apiStatus: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  setSuccessOpen: PropTypes.func.isRequired,
  setOpen: PropTypes.func.isRequired,
};

const UdReassignmentModal = ({
  open,
  setOpen,
  entry,
  companyDrivers,
  logIdPub,
  i18n,
  createUdReassignment,
  getDailyLogDetails,
  apiStatus,
  fetchDailyLogDetailsApiId,
}) => {
  const [successOpen, setSuccessOpen] = useState(false);
  const formPropsRef = useRef();

  const handleClose = () => {
    setOpen(false);
  };

  formPropsRef.current = {
    entry,
    companyDrivers,
    logIdPub,
    i18n,
    createUdReassignment,
    getDailyLogDetails,
    fetchDailyLogDetailsApiId,
    apiStatus,
    handleClose,
    setSuccessOpen,
    setOpen,
  };

  const FormContent = useCallback(() => <UdReassignmentFormContent {...formPropsRef.current} />, []);
  const SuccessContent = useCallback(
    () => (
      <div className="successpopup">
        <SuccessIcon />
        <h2>{i18n.t('logs.udReassignSuccess')}</h2>
        <div className="deletePopupBtn">
          <div className="deletebtn">
            <Button label={i18n.t('common.ok')} onClick={() => setSuccessOpen(false)} />
          </div>
        </div>
      </div>
    ),
    [i18n]
  );

  return (
    <>
      <Modal.default
        open={open}
        setOpen={(value) => {
          if (!value) handleClose();
        }}
        isSuccess
        data={{
          content: FormContent,
        }}
      />
      <Modal.default
        open={successOpen}
        setOpen={setSuccessOpen}
        data={{
          content: SuccessContent,
        }}
        isSuccess
      />
    </>
  );
};

UdReassignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  entry: PropTypes.object,
  companyDrivers: PropTypes.object,
  logIdPub: PropTypes.string,
  i18n: PropTypes.object.isRequired,
  createUdReassignment: PropTypes.func,
  getDailyLogDetails: PropTypes.func.isRequired,
  apiStatus: PropTypes.object,
  fetchDailyLogDetailsApiId: PropTypes.string,
};

export default UdReassignmentModal;
